import type React from "react";
import { createContext, useCallback, useContext, useState } from "react";

// Minimum viewport size for the app layout
export const MIN_SIZE = 375;

// Breakpoint where chat and preview can be side-by-side (2 × chat panel width of 448px)
export const SIDE_BY_SIDE_BREAKPOINT = 896;

export interface AssetSelectionRequest {
  fieldLabel: string;
  accept?: string[];
  onSelect: (assetId: string) => void;
  onCancel: () => void;
}

export const useUIValue = () => {
  const [showChat, setShowChatState] = useState(true);
  const [showProjects, setShowProjectsState] = useState(false);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [isExportHighlighted, setIsExportHighlighted] = useState(false);
  const [assetSelectionRequest, setAssetSelectionRequest] = useState<AssetSelectionRequest | null>(
    null,
  );

  const setShowChat = useCallback((show: boolean) => {
    setShowChatState(show);
  }, []);

  const setShowProjects = useCallback((show: boolean) => {
    setShowProjectsState(show);
  }, []);

  const startAssetSelection = useCallback((request: AssetSelectionRequest) => {
    setAssetSelectionRequest(request);
  }, []);

  const cancelAssetSelection = useCallback(() => {
    if (assetSelectionRequest) {
      assetSelectionRequest.onCancel();
    }
    setAssetSelectionRequest(null);
  }, [assetSelectionRequest]);

  const completeAssetSelection = useCallback(
    (assetId: string) => {
      if (assetSelectionRequest) {
        assetSelectionRequest.onSelect(assetId);
      }
      setAssetSelectionRequest(null);
    },
    [assetSelectionRequest],
  );

  const triggerExportHighlight = useCallback(() => {
    setIsExportHighlighted(true);

    // Close chat on mobile to show the toolbar
    if (window.innerWidth < SIDE_BY_SIDE_BREAKPOINT) {
      setShowChatState(false);
    }

    // Reset highlight after 3 seconds
    setTimeout(() => {
      setIsExportHighlighted(false);
    }, 3000);
  }, []);

  return {
    showChat,
    setShowChat,
    showProjects: showProjects && showChat,
    setShowProjects,
    showTopUpModal,
    setShowTopUpModal,
    assetSelectionRequest,
    startAssetSelection,
    cancelAssetSelection,
    completeAssetSelection,
    isExportHighlighted,
    triggerExportHighlight,
  };
};

type UIContextValue = ReturnType<typeof useUIValue>;

const UIContext = createContext<UIContextValue | null>(null);

export const useUI = () => {
  const ctx = useContext(UIContext);
  if (!ctx) throw new Error("useUI must be used within UIProvider");
  return ctx;
};

interface UIProviderProps {
  children: React.ReactNode;
  value: UIContextValue;
}

export const UIProvider: React.FC<UIProviderProps> = ({ children, value }) => {
  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};
